import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';

import {
  selectCompanyCode,
  selectVerificationSummary,
  selectRecentVerifications,
  getSummaryByCompanyCode,
  getRecentVerifications,
} from '../store/widgetSlice';

import BaseButton from '../components/common/BaseButton';
import HomeSummaryTile from '../components/common/HomeSummaryTile';
import SummaryTable from '../components/tables/SummaryTable';

import session from '../assets/session.svg';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    minHeight: 215,
  },
  buttonContainer: {
    paddingTop: theme.spacing(3),

    '& > div': {
      marginRight: theme.spacing(3),
    },
  },
  homeTitle: {
    display: 'block',
    color: '#C7C7C7',
    fontSize: '1.5rem',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  homeButton: {
    textTransform: 'none',
    borderRadius: '10px !important',
  },
  bold: {
    fontFamily: 'Open Sans, Helvetica, sans-serif',
    fontWeight: 600,
    display: 'block',
    letterSpacing: '-0.99px',
  },
  light: {
    fontFamily: 'Open Sans, Helvetica, sans-serif',
    fontWeight: 300,
    letterSpacing: '-0.99px',
  },
  support: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
}));

export default function Home() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const companyCode = useSelector(selectCompanyCode);
  const verificationSummary = useSelector(selectVerificationSummary);
  const recentVerifications = useSelector(selectRecentVerifications);

  useEffect(() => {
    if (companyCode) {
      dispatch(getRecentVerifications({ companyCode }));
    }
  }, [dispatch, companyCode]);

  useEffect(() => {
    if (companyCode) {
      dispatch(getSummaryByCompanyCode({ companyCode }));
    }
  }, [dispatch, companyCode]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container xs={12} style={{ maxWidth: 800, margin: '0 auto' }}>
              <Grid item xs={6}>
                <Typography
                  align="left"
                  component="p"
                  variant="h4"
                  color="primary"
                  style={{ maxWidth: 370, lineHeight: 1 }}
                >
                  <span className={classes.bold}>Protect your revenue</span>
                  <span className={classes.light}>with AI-powered guest screening and verification</span>
                </Typography>
                <Typography
                  align="left"
                  component="p"
                  variant="h6"
                  color="primary"
                  style={{ marginTop: 10, maxWidth: 370 }}
                >
                  <span className={classes.bold}>Get started now</span>
                </Typography>
                <Grid className={classes.buttonContainer} container xs={12}>
                  <BaseButton
                    IconComponent={EditIcon}
                    color="primary"
                    component={Link}
                    to="/create-verification"
                    variant="contained"
                    className={classes.homeButton}
                  >
                    Create Verification
                  </BaseButton>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <img width="258" height="207" className={classes.image} src={session} alt="Identity Verification" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <span className={classes.homeTitle}>Passed</span>
          <Paper className={classes.paper}>
            <HomeSummaryTile
              total={(verificationSummary && verificationSummary.passed) || 0}
              status="C"
              disableGraphs
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <span className={classes.homeTitle}>Pending</span>
          <Paper className={classes.paper}>
            <HomeSummaryTile
              total={(verificationSummary && verificationSummary.pending) || 0}
              status="P"
              disableGraphs
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <span className={classes.homeTitle}>Failed</span>
          <Paper className={classes.paper}>
            <HomeSummaryTile
              total={(verificationSummary && verificationSummary.failed) || 0}
              status="F"
              disableGraphs
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <span className={classes.homeTitle}>Details</span>
          <Paper className={classes.paper}>
            <SummaryTable rows={recentVerifications} />
          </Paper>
          <Grid className={classes.buttonContainer} container xs={12} md={8}>
            <BaseButton
              IconComponent={EditIcon}
              color="primary"
              component={Link}
              to="/create-verification"
              variant="contained"
              className={classes.homeButton}
            >
              Create Verification
            </BaseButton>
            <BaseButton
              component={Link}
              color="primary"
              variant="outlined"
              to="/verifications"
              className={classes.homeButton}
            >
              View All Verifications
            </BaseButton>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
